// hooks/useTutorSession.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { getBedrockClient } from '../utils/bedrockClient'; // For any other Bedrock utils
import { openAITools } from '../utils/toolConfig';
import { executeToolCall } from '../utils/toolUtils'; // Your tool executor
import { uploadImageToS3 } from '../utils/S3Utils';
import { sanitizeTextForTTS } from '../utils/mathToSpeech';

import { streamOpenAIChatCompletion } from '../utils/openAIService';
import { formatMessagesForOpenAI } from '../utils/openAIMessageHistoryUtils';
import { checkUserTtsLimits, checkUserChatLimits } from '../utils/usageChecker';
import { updateUserSpending } from '../utils/TokenUtils';

const HARD_MESSAGE_LIMIT = 40;
const OPENAI_MODEL_ID = 'gpt-4.1-mini';

function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

const AI_PROVIDER = process.env.REACT_APP_AI_PROVIDER || 'openai';

export function useTutorSession({
  auth,
  userProfile,
  customTableName,
  initialMessage,
  ttsRef,
  enableTts = false,
  onTtsLimitReached,
  sessionType = 'Tutor Chat'
}) {
  const { user, getTemporaryCredentials, bedrockRuntimeClient, getIdToken } = auth || {};
  const userId = user?.sub || null;
  const directIdToken = user?.tokens?.idToken || null;

  const [messages, setMessages] = useState(() => {
    if (initialMessage) {
      return [{ ...initialMessage, id: generateUUID(), timestamp: Date.now() }];
    }
    return [];
  });
  const [sessionId, setSessionId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [ttsLimitReached, setTtsLimitReached] = useState(false);
  const [chatLimitReached, setChatLimitReached] = useState(false);
  const [chatLimitInfo, setChatLimitInfo] = useState(null);

  const bedrockClientRef = useRef(null);
  const dynamoUtilsRef = useRef(null);
  const tableName = useRef(customTableName || 'ai-math-tutor-conversations');
  const stopStreamingRef = useRef(false);

  const fullTextRef = useRef('');
  const currentAssistantMessageIdRef = useRef(null);
  const ephemeralStatusMessageIdRef = useRef(null); // For "Creating Diagram..."
  const currentToolCallsRef = useRef([]);
  const isHandlingToolCallRef = useRef(false);

  const getOpenAITools = useCallback(() => {
    return openAITools && openAITools.length > 0 ? openAITools : undefined;
  }, []);

  useEffect(() => {
    if (user?.sub) {
      initClients();
      const handleAuthRefresh = async () => {
        if (AI_PROVIDER === 'bedrock' && bedrockClientRef.current) {
             bedrockClientRef.current = null;
        }
        await initClients();
      };
      window.addEventListener('auth:tokens-refreshed', handleAuthRefresh);
      return () => {
        window.removeEventListener('auth:tokens-refreshed', handleAuthRefresh);
      };
    }
  }, [user, AI_PROVIDER, sessionId]);

  const checkTtsLimitsInitial = async () => {
    if (!userId || !dynamoUtilsRef.current || !enableTts) {
      console.log('TTS Initial Check: Skipping - missing requirements', { userId: !!userId, dynamo: !!dynamoUtilsRef.current, enableTts });
      return true;
    }
    console.log('TTS Initial Check: Starting check for user', userId);
    try {
      const result = await checkUserTtsLimits(userId, dynamoUtilsRef.current);
      console.log('TTS Initial Check: Result', result);
      if (!result.withinLimits) {
        console.log('TTS Initial Check: Limits exceeded, disabling TTS');
        setTtsLimitReached(true);
        if (onTtsLimitReached) {
          onTtsLimitReached(result);
        }
        return false;
      }
      console.log('TTS Initial Check: Within limits, enabling TTS');
      setTtsLimitReached(false);
      if (onTtsLimitReached) {
        onTtsLimitReached(result);
      }
      return true;
    } catch (error) {
      console.error('TTS Initial Check: Failed', error);
      return true;
    }
  };

  const checkTtsLimitsBackground = () => {
    if (!userId || !dynamoUtilsRef.current) {
      console.log('TTS Background Check: Skipping - missing requirements');
      return;
    }
    console.log('TTS Background Check: Starting check for user', userId);
    checkUserTtsLimits(userId, dynamoUtilsRef.current)
      .then(result => {
        console.log('TTS Background Check: Result', result);
        if (!result.withinLimits) {
          if (!ttsLimitReached) {
            console.log('TTS Background Check: Limits exceeded, disabling TTS');
            setTtsLimitReached(true);
            if (onTtsLimitReached) onTtsLimitReached(result);
          }
        } else {
          if (ttsLimitReached) {
            console.log('TTS Background Check: Within limits, Re-enabling TTS');
            setTtsLimitReached(false);
            if (onTtsLimitReached) onTtsLimitReached(result);
          }
        }
      })
      .catch(error => {
        console.error('TTS Background Check: Failed', error);
      });
  };

  const checkChatLimitsInitial = async () => {
    if (!userId || !dynamoUtilsRef.current) {
      return true;
    }
    try {
      const result = await checkUserChatLimits(userId, dynamoUtilsRef.current);
      setChatLimitReached(!result.withinLimits);
      setChatLimitInfo(result);
      return result.withinLimits;
    } catch (err) {
      console.error('Chat Initial Check failed', err);
      return true;
    }
  };

  const checkChatLimitsBackground = () => {
    if (!userId || !dynamoUtilsRef.current) return;
    checkUserChatLimits(userId, dynamoUtilsRef.current)
      .then(result => {
        setChatLimitReached(!result.withinLimits);
        setChatLimitInfo(result);
      })
      .catch(err => console.error('Chat Background Check failed', err));
  };

  async function initClients() {
    setIsInitializing(true);
    try {
      if (!bedrockClientRef.current && bedrockRuntimeClient && AI_PROVIDER === 'bedrock') {
        bedrockClientRef.current = getBedrockClient({ client: bedrockRuntimeClient });
      }
      if (!dynamoUtilsRef.current && getTemporaryCredentials) {
        const creds = await getTemporaryCredentials();
        if (creds) {
          const region = process.env.REACT_APP_AWS_REGION || 'us-west-2';
          const { DynamoDBClient } = await import('@aws-sdk/client-dynamodb');
          const { MathTutorDynamoUtils } = await import('../utils/DynamoDBUtils');
          const ddbClient = new DynamoDBClient({ credentials: creds, region });
          const dutils = new MathTutorDynamoUtils(ddbClient);
          dutils.setS3Client(creds, region);
          dutils.setTableName(tableName.current);
          dynamoUtilsRef.current = dutils;
        }
      }
    } catch (err) {
      console.error('initClients error:', err);
    } finally {
      setIsInitializing(false);
    }
  }

  async function createNewConversation(metaUserMessageText = '', meta = {}) {
    if (!userId || !dynamoUtilsRef.current) {
      console.error('Cannot create new conversation, missing user or dynamoUtils');
      return null;
    }
    console.log('createNewConversation called with:', { metaUserMessageText, meta });
    if (!meta.topic && !meta.type) {
      console.warn('createNewConversation called without proper metadata:', meta);
      console.trace('Call stack:');
    }
    try {
      const newConversationId = `${meta.type || 'math'}_session_${Date.now()}_${generateUUID().slice(0,8)}`;
      const { topic, difficulty, chapter, section, type } = meta;
      let lessonName = '';
      if (chapter) lessonName += `Chapter ${chapter}: `;
      lessonName += topic || (type === 'homework' ? 'Homework Help' : 'General');
      if (section) lessonName += ` - ${section}`;
      const difficultyMap = { easy: 'Get the Basics', medium: 'Revision', hard: 'Become an Expert', practice: 'Practicing' };
      let displayDifficulty = difficultyMap[difficulty] || difficulty;
      if (displayDifficulty) lessonName += ` - (${displayDifficulty})`;
      const lessonNameTruncated = lessonName.length > 100 ? lessonName.substring(0, 97) + '...' : lessonName;

      await dynamoUtilsRef.current.addMetaMessage({
        conversationId: newConversationId, userId, conversationName: lessonNameTruncated,
        topic, difficulty, displayDifficulty, chapter, section, status: 'in_progress',
        type: type || 'tutor', meta_user_message: metaUserMessageText,
        moduleId: meta.moduleId, textbook: meta.textbook, yearLevel: meta.yearLevel,
        explanationLevel: meta.explanationLevel, tableName: tableName.current
      });

      if (meta.initialMessage && metaUserMessageText && isInitializing) {
         setMessages(prev => [...prev, {
          id: generateUUID(), role: 'user', content: metaUserMessageText, meta: true, timestamp: Date.now() -1
        }]);
      }
      setSessionId(newConversationId);
      return newConversationId;
    } catch (error) {
      console.error('Error creating new conversation:', error);
      return null;
    }
  }

  async function loadConversation(conversationIdToLoad) {
    if (!conversationIdToLoad || !dynamoUtilsRef.current || !userId) {
      console.error('Cannot load conversation - missing ID, user, or dynamoUtils');
      return null;
    }
    setIsInitializing(true);
    try {
      const items = await dynamoUtilsRef.current.queryConversations(
        conversationIdToLoad, userId, HARD_MESSAGE_LIMIT + 20, tableName.current, true
      );
      if (!items || items.length === 0) {
        setMessages([]); setSessionId(conversationIdToLoad); return null;
      }
      items.sort((a, b) => a.timestamp - b.timestamp);

      const metaItem = items.find(item => item.message_type === 'meta');
      let sessionMetadata = null;
      let hiddenInitialMessageContent = null;

      if (metaItem?.content && typeof metaItem.content === 'string') {
          try {
              const parsedContent = JSON.parse(metaItem.content);
              sessionMetadata = parsedContent;
              hiddenInitialMessageContent = parsedContent.meta_user_message;
          } catch (e) { console.warn('Failed to parse session metadata from metaItem.content:', e); }
      } else if (metaItem?.metadata) {
          sessionMetadata = metaItem.metadata;
          hiddenInitialMessageContent = metaItem.meta_user_message;
      }

      const loadedMsgs = items.map(msg => convertDBItemToLocal(msg)).filter(Boolean);

      if (hiddenInitialMessageContent) {
          const alreadyHasMetaMsg = loadedMsgs.some(m => m.meta && m.content === hiddenInitialMessageContent);
          if(!alreadyHasMetaMsg) {
            loadedMsgs.unshift({
              id: generateUUID(), role: 'user', content: hiddenInitialMessageContent, meta: true, timestamp: (metaItem?.timestamp || Date.now()) -1
            });
          }
      }
      setMessages(loadedMsgs);
      setSessionId(conversationIdToLoad);
      return { sessionId: conversationIdToLoad, metadata: sessionMetadata, conversationName: sessionMetadata?.conversationName };
    } catch (error) {
      console.error('loadConversation error:', error);
      return null;
    } finally {
      setIsInitializing(false);
    }
  }

  function convertDBItemToLocal(item) {
    if (!item) return null;
    const baseLocalMsg = { id: item.id || generateUUID(), timestamp: item.timestamp };

    if (item.message_type === 'openai_tool_request') {
      return { ...baseLocalMsg, role: 'assistant', content: item.content, tool_calls: item.tool_calls, message_type: 'openai_tool_request', status: null };
    }
    if (item.message_type === 'openai_tool_result') {
      return { ...baseLocalMsg, role: 'tool', tool_call_id: item.tool_call_id, name: item.name, content: item.content, message_type: 'openai_tool_result', status: 'toolResultDisplay', toolName: item.name };
    }

    if (AI_PROVIDER === 'openai' && (item.message_type === 'tool_input' || item.message_type === 'tool_result')) {
        console.warn(`Skipping Bedrock-specific tool message (type: ${item.message_type}) during load for OpenAI session.`);
        return null;
    }
    
    if (item.message_type === 'tool_result') {
        const s3Url = item.toolInfo?.s3Url || item.toolInfo?.result?.imageUrl || item.toolInfo?.result?.s3Url;
        if (s3Url) {
            return { ...baseLocalMsg, role: 'assistant', messageType: 'image', content: [{ type: 'image', url: s3Url, alt: item.toolInfo?.result?.alt || 'Generated graph', caption: item.toolInfo?.result?.caption || item.toolInfo?.name || 'Graph' }] };
        }
        return null;
    }

    if (item.message_type === 'file') {
        const fileName = item.fileInfo?.fileName || (typeof item.fileInfo === 'string' ? item.fileInfo : null) || 'unknown file';
        return { ...baseLocalMsg, role: item.role === 'user' ? 'user_attachment' : item.role, messageType: 'file', content: `Uploaded file: ${fileName}`, fileName, fileType: item.fileInfo?.fileType, fileUrl: item.fileInfo?.fileUrl || item.s3Url, imagePreviewUrl: item.fileInfo?.imageUrl };
    }
    if (item.message_type === 'image' && item.imageInfo?.imageUrl) {
        return { 
            ...baseLocalMsg, 
            role: item.role, 
            messageType: 'image', 
            content: [
                { type: 'text', text: item.content || item.imageInfo.caption || '' }, 
                { 
                    type: 'image', 
                    url: item.imageInfo.imageUrl, // This should be the S3 URL
                    alt: item.imageInfo.alt || 'Generated image', 
                    caption: item.imageInfo.caption || item.imageInfo.title 
                }
            ] 
        };
    }
    if (item.message_type === 'drawing' && item.imageInfo?.imageUrl) {
        return { ...baseLocalMsg, role: 'user', messageType: 'drawing', content: (item.imageInfo.explanation && typeof item.imageInfo.explanation === 'string' && item.imageInfo.explanation.trim() !== '') ? item.imageInfo.explanation : parsePossibleJSON(item.content), imageInfo: { imageUrl: item.imageInfo.imageUrl, alt: item.imageInfo.alt || 'Student sketch' } };
    }
    if (item.message_type === 'meta' || item.meta === true) {
        return null;
    }
    return { ...baseLocalMsg, role: item.role, messageType: item.message_type || 'text', content: parsePossibleJSON(item.content) };
  }

  function parsePossibleJSON(value) {
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try { return JSON.parse(value); } catch { /* ignore */ }
    }
    return value;
  }

  async function actualSendMessage(userTextOrMetaMessage, systemPrompt, meta, isInitializationCall) {
    if (isHandlingToolCallRef.current && AI_PROVIDER === 'openai') {
        console.warn("actualSendMessage called while already handling an OpenAI tool call. Aborting.");
        return;
    }
    if ((!userTextOrMetaMessage && !isInitializationCall) || isStreaming || (isInitializing && !isInitializationCall)) {
        if (!(isInitializing && isInitializationCall && !isStreaming)) {
            console.log("actualSendMessage call skipped due to state:", {isStreaming, isInitializing, isInitializationCall, userTextOrMetaMessage});
            return;
        }
    }

    let currentSessionIdVal = sessionId;
    if (isInitializationCall) {
        await checkTtsLimitsInitial();
        await checkChatLimitsInitial();
    }

    if (!currentSessionIdVal) {
      currentSessionIdVal = await createNewConversation(isInitializationCall ? userTextOrMetaMessage : '', meta);
      if (!currentSessionIdVal) {
        if(isInitializationCall) setIsInitializing(false);
        setIsStreaming(false);
        setMessages(prev => [...prev, { id: generateUUID(), role: 'assistant', content: 'Error: Could not start session.', status: 'error', timestamp: Date.now()}]);
        return;
      }
    }

    let userMessageForHistory = null;
    if (!isInitializationCall && userTextOrMetaMessage) {
      userMessageForHistory = { id: generateUUID(), role: 'user', content: userTextOrMetaMessage, messageType: 'text', timestamp: Date.now() };
      setMessages((prev) => [...prev, userMessageForHistory]);
      if (dynamoUtilsRef.current && userId && currentSessionIdVal) {
        await dynamoUtilsRef.current.addMessage({
          conversationId: currentSessionIdVal, userId, messageType: 'text', role: 'user',
          content: userTextOrMetaMessage, tableName: tableName.current, timestamp: userMessageForHistory.timestamp
        });
      }
    }

    const assistantMsgId = generateUUID();
    currentAssistantMessageIdRef.current = assistantMsgId;
    setMessages((prev) => [...prev, { id: assistantMsgId, role: 'assistant', content: '', status: 'thinking', timestamp: Date.now() }]);
    
    fullTextRef.current = '';
    currentToolCallsRef.current = [];
    setIsStreaming(true);
    stopStreamingRef.current = false;
    isHandlingToolCallRef.current = false; // Reset for this new turn

    // Clean up previous ephemeral status message if any
    if (ephemeralStatusMessageIdRef.current) {
      setMessages(prev => prev.filter(m => m.id !== ephemeralStatusMessageIdRef.current));
      ephemeralStatusMessageIdRef.current = null;
    }

    try {
      let dbHistory = [];
      if (dynamoUtilsRef.current && userId && currentSessionIdVal) {
        dbHistory = await dynamoUtilsRef.current.queryConversations(
          currentSessionIdVal, userId, HARD_MESSAGE_LIMIT, tableName.current, true
        );
        dbHistory.sort((a, b) => a.timestamp - b.timestamp);
      }

      const messagesForAPI = formatMessagesForOpenAI(
          [...dbHistory, ...(userMessageForHistory ? [userMessageForHistory] : [])].filter(Boolean),
          systemPrompt
      );
    
      
      if (AI_PROVIDER === 'openai') {
        let tokenToUse = directIdToken;
        if (!tokenToUse && typeof getIdToken === 'function') tokenToUse = await getIdToken();
        if (!tokenToUse) throw new Error("OpenAI Auth token not available!");

        await streamOpenAIChatCompletion({
          token: tokenToUse,
          messages: messagesForAPI,
          tools: getOpenAITools(),
          tool_choice: "auto",
          stopGenerationSignal: stopStreamingRef,
          userId: userId,
          dynamoUtils: dynamoUtilsRef.current,
          onChunk: (chunk) => {
            if (stopStreamingRef.current) return;
            const delta = chunk.choices?.[0]?.delta;
            if (!delta) return;

            if (delta.content) {
              fullTextRef.current += delta.content;
              setMessages(prev => prev.map(m => m.id === currentAssistantMessageIdRef.current ? {...m, content: fullTextRef.current, status: null } : m));
            }
            if (delta.tool_calls) {
              isHandlingToolCallRef.current = true;
              delta.tool_calls.forEach(tcDelta => {
                if (tcDelta.index >= currentToolCallsRef.current.length) {
                  currentToolCallsRef.current.push({ id: tcDelta.id, type: 'function', function: { name: tcDelta.function?.name || '', arguments: '' } });
                }
                if (tcDelta.function?.arguments) {
                  currentToolCallsRef.current[tcDelta.index].function.arguments += tcDelta.function.arguments;
                }
                // No status message update here for "thinking_tool" to keep content clean
              });
            }
          },
          onComplete: async (streamUsageData, finishReason) => {
            // Add this logging
            console.log('[Token Debug] Initial stream complete:', {
              finishReason,
              usage: streamUsageData,
              messageLength: fullTextRef.current?.length || 0,
              toolCallsCount: currentToolCallsRef.current.length
            });

            if (stopStreamingRef.current) {
                setIsStreaming(false);
                setMessages(prev => prev.map(m => m.id === currentAssistantMessageIdRef.current ? {...m, status: 'stopped', content: fullTextRef.current || "Generation stopped."} : m));
                handleStreamComplete();
                stopStreamingRef.current = false;
                isHandlingToolCallRef.current = false;
                return;
            }

            setIsStreaming(false);
            const finalToolCalls = currentToolCallsRef.current;
            const assistantMessageWithToolRequest = { // This message contains pre-tool text and the request
                id: currentAssistantMessageIdRef.current,
                role: 'assistant',
                content: fullTextRef.current || "",
                tool_calls: finalToolCalls.map(tc => ({
                    id: tc.id, type: 'function',
                    function: { name: tc.function.name, arguments: tc.function.arguments }
                })),
                timestamp: Date.now(),
                message_type: 'openai_tool_request',
                status: null // Pre-tool text is now final for this message part
            };

            if (finishReason === 'tool_calls' && finalToolCalls && finalToolCalls.length > 0) {
              // Only update UI message if there's text content
              if (fullTextRef.current && fullTextRef.current.trim().length > 0) {
                // Create the assistant message with tool calls for UI
                const assistantMessageWithToolRequest = {
                  id: currentAssistantMessageIdRef.current,
                  role: 'assistant',
                  content: fullTextRef.current,
                  tool_calls: finalToolCalls.map(tc => ({
                    id: tc.id,
                    type: 'function',
                    function: {
                      name: tc.function.name,
                      arguments: tc.function.arguments
                    }
                  })),
                  timestamp: Date.now(),
                  message_type: 'openai_tool_request'
                };

                setMessages(prev => prev.map(m => 
                  m.id === currentAssistantMessageIdRef.current 
                    ? assistantMessageWithToolRequest 
                    : m
                ));
              }
              // If no text content, leave the "thinking" message as-is for now
              
              // ALWAYS save the assistant message with tool calls to DB 
              // (even if it has no content - OpenAI requires it for proper conversation flow)
              if (dynamoUtilsRef.current) {
                await dynamoUtilsRef.current.addMessage({
                  conversationId: currentSessionIdVal,
                  userId,
                  tableName: tableName.current,
                  messageType: 'openai_tool_request',
                  role: assistantMessageWithToolRequest.role,
                  content: fullTextRef.current || null,
                  tool_calls: finalToolCalls.map(tc => ({
                    id: tc.id,
                    type: 'function',
                    function: {
                      name: tc.function.name,
                      arguments: tc.function.arguments
                    }
                  }))
                 });
               }

              let toolResultMessagesForNextAPICall = [];
              let generatedImage = false;

              for (const toolCall of finalToolCalls) {
                let toolResultContent;
                let toolExecutionResult = null;
                try {
                  const args = JSON.parse(toolCall.function.arguments);
                  toolExecutionResult = await executeToolCall(toolCall.function.name, args, { sessionId: currentSessionIdVal, userId, dynamoUtils: dynamoUtilsRef.current, saveToS3: true });
                  toolResultContent = JSON.stringify(toolExecutionResult);

                  // If tool execution was successful and created an image, add image display message
                  if (toolExecutionResult.success && toolExecutionResult.imageUrl) {
                      generatedImage = true;
                      
                      // Store image info for later use (after tool results are processed)
                      // Don't add to UI or DB yet - this breaks OpenAI conversation flow
                  }

                  // Create a compact summary for DB storage (no base64)
                  let toolResultSummaryForDB;
                  if (toolExecutionResult.success) {
                    toolResultSummaryForDB = JSON.stringify({
                      success: true,
                      type: toolCall.function.name,
                      caption: toolExecutionResult.caption || `Generated ${toolCall.function.name}`,
                      imageCreated: !!toolExecutionResult.imageUrl,
                      s3Url: toolExecutionResult.imageUrl // Store S3 URL instead of base64
                    });
                  } else {
                    toolResultSummaryForDB = JSON.stringify({ 
                      success: false, 
                      error: toolExecutionResult.error || `Failed to execute ${toolCall.function.name}` 
                    });
                  }

                  const toolResponseMessage = {
                    id: generateUUID(), 
                    role: 'tool', 
                    tool_call_id: toolCall.id, 
                    name: toolCall.function.name,
                    content: toolResultSummaryForDB, // Compact version for DB
                    timestamp: Date.now() + 3, 
                    message_type: 'openai_tool_result'
                  };
                  
                  // Don't add tool response to UI - just save to DB for conversation history
                  
                  // Save compact version to DB
                  if (dynamoUtilsRef.current) {
                    await dynamoUtilsRef.current.addMessage({ 
                      conversationId: currentSessionIdVal, 
                      userId, 
                      tableName: tableName.current,
                      // Pass all the required OpenAI fields
                      messageType: toolResponseMessage.message_type,
                      role: toolResponseMessage.role,
                      content: toolResponseMessage.content,
                      tool_call_id: toolResponseMessage.tool_call_id,
                      name: toolResponseMessage.name,
                      message_type: toolResponseMessage.message_type
                    });
                  }
                  
                  // NOW add image display message AFTER tool result is saved
                  if (toolExecutionResult.success && toolExecutionResult.imageUrl) {
                      // Update the existing "thinking" message to include the image
                      setMessages(prev => prev.map(m => 
                        m.id === currentAssistantMessageIdRef.current 
                          ? {
                              ...m,
                              messageType: 'image',
                              content: [
                                  // Include any pre-tool text if it exists
                                  ...(fullTextRef.current && fullTextRef.current.trim().length > 0 
                                      ? [{type: 'text', text: fullTextRef.current}] 
                                      : []),
                                  // Add the image caption
                                  {type: 'text', text: toolExecutionResult.caption || `Generated ${toolCall.function.name}`},
                                  // Add the image
                                  {type: 'image', url: toolExecutionResult.imageUrl, alt: toolExecutionResult.alt || toolCall.function.name, caption: toolExecutionResult.caption}
                              ],
                              status: null,
                              timestamp: Date.now() + 10
                          }
                          : m
                      ));
                      
                      // For DB storage (compact version with S3 URL only)
                     const compactImageMessageForDB = {
                          id: generateUUID(),
                          role: 'assistant', 
                          messageType: 'image',
                          content: toolExecutionResult.caption || `Generated ${toolCall.function.name}`,
                          imageInfo: {
                              imageUrl: toolExecutionResult.imageUrl, // S3 URL
                              alt: toolExecutionResult.alt || toolCall.function.name,
                              caption: toolExecutionResult.caption,
                              title: toolExecutionResult.caption
                          },
                          timestamp: Date.now() + 10,
                          message_type: 'image'
                      };
                      

                      if (dynamoUtilsRef.current) {
                          await dynamoUtilsRef.current.addMessage({ 
                              ...compactImageMessageForDB, 
                              conversationId: currentSessionIdVal, 
                              userId, 
                              tableName: tableName.current 
                          });
                      }
                  }
                  
                  // For the follow-up API call, use the full result (with base64)
                  toolResultMessagesForNextAPICall.push({ 
                    role: 'tool', 
                    tool_call_id: toolResponseMessage.tool_call_id, 
                    name: toolResponseMessage.name, 
                    content: toolResultContent // Full result for API (with base64)
                  });
                  
                } catch (error) {
                  console.error(`Error executing or parsing args for tool ${toolCall.function.name}:`, error);
                  toolResultContent = JSON.stringify({ success: false, error: `Failed to execute ${toolCall.function.name}: ${error.message}` });
                  
                  const toolResponseMessage = {
                    id: generateUUID(), 
                    role: 'tool', 
                    tool_call_id: toolCall.id, 
                    name: toolCall.function.name,
                    content: toolResultContent, 
                    timestamp: Date.now() + 3, 
                    message_type: 'openai_tool_result'
                  };
                  setMessages(prev => [...prev, toolResponseMessage]);
                  if (dynamoUtilsRef.current) {
                    await dynamoUtilsRef.current.addMessage({ ...toolResponseMessage, conversationId: currentSessionIdVal, userId, tableName: tableName.current });
                  }
                  toolResultMessagesForNextAPICall.push({ 
                    role: 'tool', 
                    tool_call_id: toolResponseMessage.tool_call_id, 
                    name: toolResponseMessage.name, 
                    content: toolResponseMessage.content 
                  });
                }
              }
              
              // Remove ephemeral "Creating Diagram..." message
              if (ephemeralStatusMessageIdRef.current) {
                  setMessages(prev => prev.filter(m => m.id !== ephemeralStatusMessageIdRef.current));
                  ephemeralStatusMessageIdRef.current = null;
              }
            

              const latestDbHistory = await dynamoUtilsRef.current.queryConversations(
                currentSessionIdVal, userId, HARD_MESSAGE_LIMIT, tableName.current, true
              );
              latestDbHistory.sort((a, b) => a.timestamp - b.timestamp);
              const messagesForFinalAPICall = formatMessagesForOpenAI(latestDbHistory, systemPrompt);

              // Add this comprehensive logging block
              console.log('[Token Debug] Tool follow-up conversation analysis:', {
                latestDbHistoryCount: latestDbHistory.length,
                messagesForFinalAPICallCount: messagesForFinalAPICall.length,
                totalCharacters: JSON.stringify(messagesForFinalAPICall).length,
                estimatedTokens: Math.ceil(JSON.stringify(messagesForFinalAPICall).length / 4),
                toolResultsAdded: toolResultMessagesForNextAPICall.length,
                latestDbHistoryDetails: latestDbHistory.map((msg, idx) => ({
                  index: idx,
                  role: msg.role,
                  messageType: msg.message_type,
                  contentLength: typeof msg.content === 'string' ? msg.content.length : JSON.stringify(msg.content || '').length,
                  hasToolCalls: !!msg.tool_calls,
                  toolCallsCount: msg.tool_calls ? msg.tool_calls.length : 0
                })),
                messagesForFinalAPICallDetails: messagesForFinalAPICall.map((msg, idx) => ({
                  index: idx,
                  role: msg.role,
                  contentLength: typeof msg.content === 'string' ? msg.content.length : JSON.stringify(msg.content || '').length,
                  hasToolCalls: !!msg.tool_calls,
                  toolCallsCount: msg.tool_calls ? msg.tool_calls.length : 0
                }))
              });
              // ADD THIS NEW LOGGING BLOCK - Full message history being sent to OpenAI
              console.log('=== FULL MESSAGE HISTORY SENT TO OPENAI ===');
              console.log(JSON.stringify(messagesForFinalAPICall, null, 2));
              console.log('=== END MESSAGE HISTORY ===');

              fullTextRef.current = ''; 
              currentToolCallsRef.current = [];
              
              const finalAssistantMsgId = generateUUID();
              currentAssistantMessageIdRef.current = finalAssistantMsgId; // New ID for final response
              // Only add thinking message if we expect a textual follow-up
              // For now, let's assume there might be one.
              setMessages((prev) => [...prev, { id: finalAssistantMsgId, role: 'assistant', content: '', status: 'thinking', timestamp: Date.now() + 4 }]);
              setIsStreaming(true);

              await streamOpenAIChatCompletion({
                token: tokenToUse,
                messages: messagesForFinalAPICall,
                tools: getOpenAITools(), 
                tool_choice: "auto",
                stopGenerationSignal: stopStreamingRef,
                userId: userId,
                dynamoUtils: dynamoUtilsRef.current,
                onChunk: (chunk) => {
                  
                  if (stopStreamingRef.current) return;
                  const deltaContent = chunk.choices?.[0]?.delta?.content;
                  if (deltaContent) {
                      fullTextRef.current += deltaContent;
                      setMessages(prev => prev.map(m => m.id === finalAssistantMsgId ? {...m, content: fullTextRef.current, status: null } : m));
                  }
                  const deltaToolCalls = chunk.choices?.[0]?.delta?.tool_calls;
                  if (deltaToolCalls) {
                      console.warn("Received chained tool_calls.");
                       isHandlingToolCallRef.current = true;
                       deltaToolCalls.forEach(tcDelta => {
                           if (tcDelta.index >= currentToolCallsRef.current.length) {
                               currentToolCallsRef.current.push({ id: tcDelta.id, type: 'function', function: { name: tcDelta.function?.name || '', arguments: '' } });
                           }
                           if (tcDelta.function?.arguments) {
                               currentToolCallsRef.current[tcDelta.index].function.arguments += tcDelta.function.arguments;
                           }
                       });
                  }
                },   
                onComplete: async (finalUsageData, finalFinishReason) => {
                  console.log('[Tool Follow-up Complete]', {
                    finalFinishReason,
                    finalContentLength: fullTextRef.current?.length || 0,
                    usage: finalUsageData,
                    assistantMsgId: finalAssistantMsgId
                  });

                  setIsStreaming(false);
                  isHandlingToolCallRef.current = false;
                  const finalContent = fullTextRef.current;

                  if (finalFinishReason === 'tool_calls' && currentToolCallsRef.current.length > 0) {
                      console.warn("Chained tool call requested. Current flow will save and stop here for this turn.");
                      // Only save if there's actual content OR tool calls
                      if ((finalContent && finalContent.trim().length > 0) || currentToolCallsRef.current.length > 0) {
                        const chainedToolCallAssistantMsg = {
                            id: finalAssistantMsgId, role: 'assistant', content: finalContent || "",
                            tool_calls: currentToolCallsRef.current.map(tc => ({id: tc.id, type: 'function', function: {name: tc.function.name, arguments: tc.function.arguments}})),
                            timestamp: Date.now(), message_type: 'openai_tool_request', status: null
                        };
                        setMessages(prev => prev.map(m => m.id === finalAssistantMsgId ? chainedToolCallAssistantMsg : m));
                        if (dynamoUtilsRef.current) {
                            await dynamoUtilsRef.current.addMessage({...chainedToolCallAssistantMsg, conversationId: currentSessionIdVal, userId, tableName: tableName.current});
                        }
                      } else {
                        // Remove empty message if no content and no tool calls
                        setMessages(prev => prev.filter(m => m.id !== finalAssistantMsgId));
                      }
                  } else if (finalContent && finalContent.trim().length > 0) {  
                      // Final response with actual content - save it
                      const finalAssistantResponse = {
                          id: finalAssistantMsgId, role: 'assistant', content: finalContent,
                          status: stopStreamingRef.current ? 'stopped' : null, timestamp: Date.now(), message_type: 'text'
                      };
                      setMessages(prev => prev.map(m => m.id === finalAssistantMsgId ? finalAssistantResponse : m));
                      if (dynamoUtilsRef.current && !stopStreamingRef.current) {
                          await dynamoUtilsRef.current.addMessage({ ...finalAssistantResponse, conversationId: currentSessionIdVal, userId, tableName: tableName.current });
                      }
                  } else {
                      // No content from final response - remove the empty "thinking" message
                      console.log('[Empty Message Fix] Removing empty assistant message after tool follow-up');
                      setMessages(prev => prev.filter(m => m.id !== finalAssistantMsgId));
                  }

                  if (finalUsageData && userId && dynamoUtilsRef.current) {
                      logTokenUsage('INITIAL_STREAM', finalUsageData, sessionType);
                      await updateUserSpending(finalUsageData, userId, OPENAI_MODEL_ID, dynamoUtilsRef.current, sessionType);
                      checkChatLimitsBackground();
                  }
                  handleStreamComplete();
                  stopStreamingRef.current = false;
                },
                onError: (err) => {
                  setIsStreaming(false); isHandlingToolCallRef.current = false;
                  handleStreamError(err, finalAssistantMsgId);
                  stopStreamingRef.current = false;
                  // Remove thinking placeholder on error if it exists
                  setMessages(prev => prev.filter(m => m.id !== finalAssistantMsgId || (m.id === finalAssistantMsgId && m.status !== 'thinking')));
                }
              });

            } else if (finishReason === 'stop' || (fullTextRef.current && fullTextRef.current.trim().length > 0)) {
              isHandlingToolCallRef.current = false;
              const assistantFinalTextResponse = {
                  id: currentAssistantMessageIdRef.current,
                  role: 'assistant',
                  content: fullTextRef.current,
                  status: stopStreamingRef.current ? 'stopped' : null,
                  timestamp: Date.now(),
                  message_type: 'text'
              };
              if (fullTextRef.current.trim().length > 0 || stopStreamingRef.current) { // Only update/add if there's actual content or it was stopped
                  setMessages(prev => prev.map(m => m.id === currentAssistantMessageIdRef.current ? assistantFinalTextResponse : m));
                  if (dynamoUtilsRef.current && fullTextRef.current.trim().length > 0 && !stopStreamingRef.current) {
                      await dynamoUtilsRef.current.addMessage({ ...assistantFinalTextResponse, conversationId: currentSessionIdVal, userId, tableName: tableName.current });
                  }
              } else {
                  // If no text content was streamed and not stopped, remove the placeholder
                  setMessages(prev => prev.filter(m => m.id !== currentAssistantMessageIdRef.current));
              }
              if (streamUsageData && userId && dynamoUtilsRef.current) {
                 await updateUserSpending(streamUsageData, userId, OPENAI_MODEL_ID, dynamoUtilsRef.current, sessionType);
                 checkChatLimitsBackground();
              }
              handleStreamComplete();
              stopStreamingRef.current = false;
            } else { // Other finish reasons or unexpected empty response (no tool calls here)
                isHandlingToolCallRef.current = false;
                console.warn("[OpenAI onComplete] Unexpected state or empty response. FinishReason:", finishReason, "Text:", fullTextRef.current);
                // Remove the placeholder if it resulted in no content and wasn't a tool_call trigger
                 if (!finalToolCalls || finalToolCalls.length === 0) {
                    setMessages(prev => prev.filter(m => m.id !== currentAssistantMessageIdRef.current));
                 }
                handleStreamComplete();
                stopStreamingRef.current = false;
            }
          },
          onError: (err) => {
            setIsStreaming(false); isHandlingToolCallRef.current = false;
            handleStreamError(err, currentAssistantMessageIdRef.current);
            stopStreamingRef.current = false;
          },
        });

      } else if (AI_PROVIDER === 'bedrock') {
        // ... (Bedrock logic remains unchanged)
        if (!bedrockClientRef.current) throw new Error("Bedrock client not initialized.");
        let finalSystemPrompt = systemPrompt;
        if (userProfile?.textbook) finalSystemPrompt += `\n\nUser's textbook: ${userProfile.textbook}`;
        
        await bedrockClientRef.current.streamChat(finalSystemPrompt, {
          toolContext: { sessionId: currentSessionIdVal, userId, tableName: tableName.current },
          userText: isInitializationCall ? '' : userTextOrMetaMessage,
          conversationId: currentSessionIdVal,
          userAttributes: { userId, sessionId: currentSessionIdVal, tableName: tableName.current },
          onChunk: (bedrockChunk) => {
              if (bedrockChunk.type === 'textChunk' && currentAssistantMessageIdRef.current) {
                fullTextRef.current += bedrockChunk.content;
                setMessages(prevMessages =>
                  prevMessages.map(msg =>
                    msg.id === currentAssistantMessageIdRef.current
                      ? { ...msg, content: (typeof msg.content === 'string' ? msg.content : '') + bedrockChunk.content, status: null }
                      : msg
                  )
                );
              } else { /* other bedrock chunk types */ }
          },
          onComplete: (finalBedrockConversation) => {
            setIsStreaming(false); handleStreamComplete(finalBedrockConversation); stopStreamingRef.current = false;
          },
          onError: (err) => {
            setIsStreaming(false); handleStreamError(err, currentAssistantMessageIdRef.current); stopStreamingRef.current = false;
          },
          stopGenerationSignal: stopStreamingRef,
        });
      }
    } catch (error) {
      console.error(`Error in actualSendMessage (${AI_PROVIDER}):`, error);
      setIsStreaming(false); isHandlingToolCallRef.current = false;
      setMessages(prev => prev.map(m => m.id === currentAssistantMessageIdRef.current ? {...m, status: 'error', content: `Error: ${error.message}` } : m).filter(Boolean));
      if(isInitializationCall) setIsInitializing(false);
      stopStreamingRef.current = false;
      handleStreamError(error, currentAssistantMessageIdRef.current);
    }
  }

  const sendMessage = async (userText, systemPrompt, meta = {}) => {
    if (isHandlingToolCallRef.current && AI_PROVIDER === 'openai') {
        console.warn("sendMessage: Attempted to send message while an OpenAI tool call is being processed. Please wait.");
        return;
    }
    // Clean up previous ephemeral status message if any, as a new send is starting
    if (ephemeralStatusMessageIdRef.current) {
        setMessages(prev => prev.filter(m => m.id !== ephemeralStatusMessageIdRef.current));
        ephemeralStatusMessageIdRef.current = null;
    }
    const isInitialization = meta?.initialMessage || false;
    await actualSendMessage(userText, systemPrompt, meta, isInitialization);
  };

  const handleStreamComplete = useCallback((finalConversation) => {
    if (enableTts && !ttsLimitReached && ttsRef?.current && fullTextRef.current.trim()) {
      const sanitizedText = sanitizeTextForTTS(fullTextRef.current);
      ttsRef.current.speak(sanitizedText).catch(err => console.error('TTS: Error during speak:', err));
    }
    checkTtsLimitsBackground();
    checkChatLimitsBackground();
    console.log(`--- ${AI_PROVIDER.toUpperCase()} RESPONSE/SEQUENCE COMPLETE ---`);
    if (AI_PROVIDER === 'bedrock' && finalConversation) {
        // Bedrock specific logging
    }
  }, [enableTts, ttsLimitReached, ttsRef, AI_PROVIDER]);

  const handleStreamError = useCallback((error, messageIdToUpdate) => {
    const idToUse = messageIdToUpdate || currentAssistantMessageIdRef.current;
    console.error(`Stream error for message ${idToUse} (${AI_PROVIDER}):`, error);
    setMessages(prev => prev.map(m => m.id === idToUse ? {...m, status: 'error', content: `Error: ${error.message || 'Unknown streaming error'}` } : m).filter(Boolean));
    fullTextRef.current = '';
    currentToolCallsRef.current = []; // Clear any pending tool calls
    isHandlingToolCallRef.current = false;

    // Clean up ephemeral status message on error
    if (ephemeralStatusMessageIdRef.current) {
        setMessages(prev => prev.filter(m => m.id !== ephemeralStatusMessageIdRef.current));
        ephemeralStatusMessageIdRef.current = null;
    }
  }, [AI_PROVIDER]);

  function stopGeneration() {
    stopStreamingRef.current = true;
    console.log("Stop generation requested.");
    // If there's an ephemeral status message, remove it on manual stop
    if (ephemeralStatusMessageIdRef.current) {
        setMessages(prev => prev.filter(m => m.id !== ephemeralStatusMessageIdRef.current));
        ephemeralStatusMessageIdRef.current = null;
    }
  }

  async function markSessionCompleted() {
    // ... (unchanged)
    if (!dynamoUtilsRef.current || !userId || !sessionId) return false;
    try {
      const items = await dynamoUtilsRef.current.queryConversations(sessionId, userId, 1, tableName.current, true, true /* metaOnly */);
      const metaItem = items.find(item => item.message_type === 'meta');
      if (!metaItem) { console.warn('No meta item found for markSessionCompleted:', sessionId); return false; }
      let content = {};
      try { content = typeof metaItem.content === 'string' ? JSON.parse(metaItem.content) : metaItem.content || {}; }
      catch (err) { content = metaItem.metadata || {}; } 
      content.status = 'completed';
      content.lastUpdated = Date.now();
      return await dynamoUtilsRef.current.updateMetaItem(sessionId, userId, { content: JSON.stringify(content), tableName: tableName.current });
    } catch (error) {
      console.error('Error marking session as completed:', error); return false;
    }
  }

  async function handleFileUpload(fileData, options = {}) {
    // ... (unchanged)
    const fileSessionId = options.sessionId || sessionId;
    const fileUserId = options.userId || userId;
    if (!fileData || !dynamoUtilsRef.current || !fileUserId || !fileSessionId) {
      console.error('Missing required data for file upload'); return false;
    }
    try {
      const fileMessage = {
        id: generateUUID(), role: 'user_attachment', messageType: 'file',
        content: `Uploaded file: ${fileData.fileName}`, fileName: fileData.fileName,
        fileType: fileData.fileType, fileUrl: fileData.fileUrl, imagePreviewUrl: fileData.imagePreviewUrl,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, fileMessage]);
      
      let contentText = fileData.text || `File uploaded: ${fileData.fileName}.`;
      const MAX_TEXT_LENGTH = 100000;
      if (contentText.length > MAX_TEXT_LENGTH) {
        contentText = contentText.substring(0, MAX_TEXT_LENGTH) + "\n\n[Content truncated due to size limitations.]";
      }
      
      let prefix = `[User uploaded a file: ${fileData.fileName}. Analyzing file...]`;
      let suffix = 'File analysis complete.';
      let augmentedContent = `${prefix}\n\n${contentText}\n\n${suffix}`;
      
      const fileInfoForDB = { fileName: fileData.fileName, fileType: fileData.fileType, fileUrl: fileData.fileUrl, extractedText: contentText };
      await dynamoUtilsRef.current.addMessage({
        conversationId: fileSessionId, userId: fileUserId, messageType: 'file',
        role: 'assistant', 
        content: augmentedContent, fileInfo: fileInfoForDB, tableName: tableName.current, timestamp: Date.now()
      });
      return true;
    } catch (error) {
      console.error('Error handling file upload:', error); return false;
    }
  }

  async function handleDrawPadSend(canvasDataUrl, { sessionId: drawSessionId, userId: drawUserId, textMessage = '' }) {
    // ... (unchanged)
    if (!canvasDataUrl || !drawSessionId || !drawUserId) {
      throw new Error('Cannot send drawing without required information.');
    }
    const ddb = dynamoUtilsRef.current;
    if (!ddb) {
      throw new Error('DynamoDB utilities not ready for drawpad send.');
    }

    const imageUrl = await uploadImageToS3(canvasDataUrl, { sessionId: drawSessionId, userId: drawUserId });
    if (!imageUrl) throw new Error('Image upload failed for drawing.');

    const userDrawingMessage = {
        id: generateUUID(), role: 'user', messageType: 'drawing',
        content: textMessage.trim() || '',
        imageInfo: { imageUrl, alt: 'Student sketch', explanation: textMessage.trim() },
        timestamp: Date.now()
    };
    setMessages((prev) => [...prev, userDrawingMessage]);

    await ddb.addMessage({
        ...userDrawingMessage,
        conversationId: drawSessionId, userId: drawUserId, tableName: tableName.current
    });
    
    let assistantContextMessageContent = `[User has provided a sketch. Image available at: ${imageUrl}. User text: "${textMessage.trim()}"]`;
    if (AI_PROVIDER === 'bedrock' && bedrockClientRef.current) {
        try {
          const description = await bedrockClientRef.current.describeImage(imageUrl, drawUserId);
          assistantContextMessageContent = `[Image analysis of user's sketch (Bedrock): ${description}. User text: "${textMessage.trim()}"]`;
        } catch (error) { console.error('Failed to get image description from Bedrock for drawpad:', error); }
    }
    
    const assistantContextMessage = {
        id: generateUUID(), role: 'assistant', messageType: 'text',
        content: assistantContextMessageContent, meta: true, 
        timestamp: Date.now()
    };
    setMessages(prev => [...prev, assistantContextMessage]);
    await ddb.addMessage({
        ...assistantContextMessage,
        conversationId: drawSessionId, userId: drawUserId, tableName: tableName.current
    });
    
    const systemPromptForDrawing = "The user has provided a drawing and accompanying text. You have a description or reference to the drawing. Respond to the user's input, incorporating the visual information as described to help with their math problem.";
    await actualSendMessage(textMessage.trim() || "Please look at my drawing.", systemPromptForDrawing, { sessionId: drawSessionId }, false);
  }

  // In useTutorSession.js, add comprehensive logging
  const logTokenUsage = (stage, data, category) => {
    console.log(`[TOKEN TRACKING] ${stage}:`, {
      timestamp: new Date().toISOString(),
      category,
      usage: data,
      sessionId: sessionId,
      isHandlingTool: isHandlingToolCallRef.current,
      stackTrace: new Error().stack.split('\n').slice(1, 4)
    });
  };

  return {
    messages, sessionId, isStreaming, isInitializing,
    createNewConversation, loadConversation, sendMessage, stopGeneration,
    markSessionCompleted, handleDrawPadSend, handleFileUpload,
    setMessages, setSessionId, ttsLimitReached,
    chatLimitReached, chatLimitInfo
  };
}